import cx from 'classnames';
import type { FieldProps, FormikHelpers } from 'formik';
import { Field, Formik } from 'formik';
import React, { useContext } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useHistory } from 'react-router';
import styled, { useTheme } from 'styled-components';

import { updateUserDetails } from '../../api/Optimise';
import infoIcon from '../../assets/svg/info-primary.svg';
import { AppContext } from '../../context/AppContext';
import ErrorLabel from '../../ui/ErrorLabel';
import LoopSpinner from '../../ui/LoopSpinner';
import StyledCheckBox from '../../ui/Styled/StyledCheckbox';
import { max, min } from '../../utils/breakpoints';

interface FormValues {
  confirm: boolean;
}

const StyledConfirmation = styled.div`
  margin-left: 32px;
  font-size: 14px;

  ${min.sm} {
    font-size: 16px;
  }
`;

const StyledImg = styled.img`
  width: 100%;
  max-width: 60px;

  ${max.sm} {
    width: 100%;
    max-width: 60px;
  }
`;

const SmartTariffSwitchWarning = () => {
  const theme = useTheme();
  const history = useHistory();
  const { userSub } = useContext(AppContext);
  const searchParams = new URLSearchParams(location.search);
  const next = searchParams.get('next');

  const returnPath = next || '/app/settings';

  const onSubmit = (values: FormValues, { setErrors, setSubmitting }: FormikHelpers<FormValues>): void => {
    updateUserDetails(
      {
        simulation_mode: values.confirm,
      },
      userSub
    )
      .then(() => {
        history.push(returnPath);
      })
      .catch((e) => {
        console.error(e);
        setSubmitting(false);
        setErrors({ confirm: 'Something went wrong. Please try again.' });
      });
  };

  return (
    <>
      <div className="mb-3">
        <StyledImg src={infoIcon} alt="Info icon" />
      </div>
      <h1 className="mb-3">{theme.language.appName} will now be switched on</h1>
      <div>
        <p>Now that you've added your smart tariff details, {theme.language.appName} will be switched on.</p>
        <p>If you'd prefer to keep {theme.language.appName} off for now, please check the box below.</p>
        <div className="text-left mt-4">
          <Formik<FormValues>
            onSubmit={onSubmit}
            initialValues={{
              confirm: false,
            }}
            validateOnChange
          >
            {({ handleSubmit, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                {!isSubmitting ? (
                  <>
                    <Field name="confirm">
                      {({ field, meta }: FieldProps<FormValues>) => (
                        <StyledCheckBox
                          className={`text-left ${cx({
                            selected: field.value,
                            error: !!meta.error,
                          })}`}
                        >
                          <Form.Check
                            type="checkbox"
                            name="confirm"
                            id="confirm-check"
                            onChange={(e: React.ChangeEvent) => {
                              return field.onChange(e);
                            }}
                            onBlur={field.onBlur}
                            className={cx({ error: !!meta.error })}
                            label={
                              <StyledConfirmation>
                                Keep {theme.language.appName} off until I switch it on in Settings.
                              </StyledConfirmation>
                            }
                          />
                          {meta.error && meta.touched && <ErrorLabel pad={false} showIcon={false} label={meta.error} />}
                        </StyledCheckBox>
                      )}
                    </Field>
                    <Button variant="primary" disabled={isSubmitting} block type="submit">
                      Continue
                    </Button>
                  </>
                ) : (
                  <LoopSpinner />
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default SmartTariffSwitchWarning;
